import { useState, useEffect, useCallback, useRef } from 'react';
import { FeedDto } from '@/api/feed';
import api from '@/api';
import { useEventBus } from '@/hooks/use-event-bus';

// Event name for feed like status changes (must match the one in useFeedLike)
const FEED_LIKE_EVENT = 'feed-like-status-change';

// Event payload type
interface FeedLikeEvent {
  feedId: string;
  isLiked: boolean;
  likeCount?: number;
}

// Event name for feed comment status changes (must match the one in useComments)
const FEED_COMMENT_EVENT = 'feed-comment-status-change';

// Event payload type
interface FeedCommentEvent {
  feedId: string;
  commentCount: number;
}

/**
 * Hook for fetching feed data
 */
export const useFeedData = (feedId: string, initialFeed?: FeedDto | null) => {
  const [feed, setFeed] = useState<FeedDto | null>(initialFeed ?? null);
  // 서버에서 받은 초기 데이터가 있으면 첫 렌더부터 본문을 그린다.
  const [isLoading, setIsLoading] = useState(!initialFeed);
  const [error, setError] = useState<string | null>(null);
  // 서버 데이터가 있는 동안의 재조회는 스켈레톤/에러 화면으로 내용을 가리지 않는다.
  const hasServerFeedRef = useRef(!!initialFeed);

  // Function to fetch feed data
  const fetchFeed = useCallback(async () => {
    if (feedId) {
      try {
        setError(null);
        if (!hasServerFeedRef.current) setIsLoading(true);
        const feedData = await api.feed.get(feedId);
        setFeed(feedData);

      } catch (error) {
        console.error('Error fetching feeds:', error);
        if (!hasServerFeedRef.current) setError('피드를 불러오는 중 오류가 발생했습니다.');
      } finally {
        hasServerFeedRef.current = false;
        setIsLoading(false);
      }
    }
  }, [feedId]);

  // Listen for feed like events
  useEventBus<FeedLikeEvent>(FEED_LIKE_EVENT, (data) => {
    // Only edit if this is for the current feed
    if (feed && data.feedId === feedId) {
      setFeed(prevFeed => {
        if (!prevFeed) return null;

        // Calculate the new like count
        let newLikeCount = prevFeed.likeCount || 0;
        if (data.isLiked && !prevFeed.isLike) {
          // If liked and wasn't liked before, increment
          newLikeCount += 1;
        } else if (!data.isLiked && prevFeed.isLike) {
          // If unliked and was liked before, decrement
          newLikeCount = Math.max(0, newLikeCount - 1);
        }

        // Return updated feed with new like status and count
        return {
          ...prevFeed,
          isLike: data.isLiked,
          likeCount: newLikeCount
        };
      });
    }
  });

  // Listen for feed comment events
  useEventBus<FeedCommentEvent>(FEED_COMMENT_EVENT, (data) => {
    // Only edit if this is for the current feed
    if (feed && data.feedId === feedId) {
      setFeed(prevFeed => {
        if (!prevFeed) return null;

        // Return updated feed with new comment count
        return {
          ...prevFeed,
          commentCount: data.commentCount
        };
      });
    }
  });

  // Fetch feed data on mount
  useEffect(() => {
    fetchFeed().then();
  }, [fetchFeed]);

  return {
    feed,
    isLoading,
    error,
    fetchFeed,
  };
};
